import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetAlertsQuery } from "store/api/alertsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { getDateRangeFromOption } from "utils/dateUtils"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { getDateRangeParams } from "components/common/DateRangeTabsFilter/dateRangeFilterUtils"
import { WidgetAlertStatuses } from "components/widgets/WidgetAlertStatuses"
import { WidgetMonitoringAlerts } from "pages/contractor/ContractorDashboard/AlertsSection/WidgetMonitoringAlerts"

import { AlertsPageContent } from "./AlertsPageContent"
import { dummyAlerts, dummyMeta } from "./dummyAlertsData"

import styles from "./index.module.scss"

const DATE_PARAM_NAME = "date"

const AlertsPage = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [triggerGetAlerts, { isFetching, data }] = useLazyGetAlertsQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const isFirstRunRef = useRef(true)
  const [updateIndex, setUpdateIndex] = useState(0)

  useEffect(() => {
    const startKey = `${DATE_PARAM_NAME}_start`
    const endKey = `${DATE_PARAM_NAME}_end`
    if (!urlQueryParams[startKey] && !urlQueryParams[endKey]) {
      setUrlQueryParams(getDateRangeParams("last-7-days", DATE_PARAM_NAME))
    }
  }, [])

  const hasAlertsAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.Alerts) : false

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetAlerts(params)
      const error = getErrorMessage(res?.error)
      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false
      const startKey = `${DATE_PARAM_NAME}_start`
      const endKey = `${DATE_PARAM_NAME}_end`
      if (!urlQueryParams[startKey] && !urlQueryParams[endKey]) {
        return
      }
    }
    fetchAlerts()
  }, [urlQueryParams])

  const widgetDateRangeParams = useMemo(() => {
    return getDateRangeFromOption("all")
  }, [])

  const fetchAlerts = () => {
    if (hasAlertsAccess) {
      sendRequest(urlQueryParams)
    }
  }

  const getAlerts = () => {
    if (!hasAlertsAccess) {
      return dummyAlerts
    }

    return data?.data || []
  }

  const handleRefreshAlerts = () => {
    fetchAlerts()
    setUpdateIndex(prev => prev + 1)
  }

  const alerts = getAlerts()
  const meta = hasAlertsAccess ? data?.meta : dummyMeta
  const isFetchingData = isFetching && !data

  return (
    <>
      <div className={classNames(styles.widgetsWrapper)}>
        <WidgetMonitoringAlerts dateRange={widgetDateRangeParams} unsupported={!hasAlertsAccess} />
        <WidgetAlertStatuses hasAlertsAccess={hasAlertsAccess} updateIndex={updateIndex} />
      </div>
      <AlertsPageContent
        alertsList={alerts}
        isFetching={isFetchingData}
        meta={meta}
        onRefreshAlerts={handleRefreshAlerts}
        urlQueryParams={urlQueryParams}
      />
      {!hasAlertsAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.Alerts} />}
    </>
  )
}

export default AlertsPage
